import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { Play, ArrowRight, Disc3 } from "lucide-react";
import { api, resolveFileUrl, contentToQueue } from "../api";
import { usePlayer } from "../contexts";
import FeaturedVideoHero from "../components/FeaturedVideoHero";

export default function HomePage() {
  const [mixtapes, setMixtapes] = useState([]);
  const [singles, setSingles] = useState([]);
  const [videos, setVideos] = useState([]);
  const [artists, setArtists] = useState([]);
  const [products, setProducts] = useState([]);
  const [banner, setBanner] = useState(null);
  const [featured, setFeatured] = useState({ creatives: [], talent: [], trending: [], top_rated: [], recently_hired: [] });
  const [marquee, setMarquee] = useState({ text: "", speed: 60, enabled: true });
  const { playList } = usePlayer();

  useEffect(() => {
    Promise.all([
      api.get("/content/mixtapes"),
      api.get("/content/singles"),
      api.get("/content/videos"),
      api.get("/artists"),
      api.get("/products"),
      api.get("/banners?placement=home_top"),
      api.get("/culture-connect/featured"),
      api.get("/marquee"),
    ]).then(([m, s, v, a, p, b, f, mq]) => {
      setMixtapes(m.data);
      setSingles(s.data);
      setVideos(v.data);
      setArtists(a.data);
      setProducts(p.data);
      setBanner(b.data?.[0] || null);
      setFeatured(f.data);
      setMarquee(mq.data || { text: "", speed: 60, enabled: true });
    }).catch(() => {});
  }, []);

  const playSingle = (s) =>
    playList(
      singles
        .filter((x) => x.file_url || x.external_url)
        .map((x) => ({
          id: x.id, title: x.title, artist: x.artist,
          cover: x.cover_image, url: x.file_url, ctype: "singles",
        })),
      singles.findIndex((x) => x.id === s.id)
    );

  const featuredVideo = videos.find((v) => v.featured) || videos[0];
  const nextVideos = videos.filter((v) => v.id !== featuredVideo?.id).slice(0, 3);
  const featuredMixtape = mixtapes.find((m) => m.featured) || mixtapes[0];

  return (
    <div className="pb-32">
      {/* FEATURED VIDEO HERO */}
      <FeaturedVideoHero video={featuredVideo} />

      {/* 3 NEXT-UP VIDEOS UNDER HERO */}
      {nextVideos.length > 0 && (
        <section className="border-b border-neutral-800" data-testid="home-next-videos">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {nextVideos.map((v) => (
                <Link
                  key={v.id}
                  to={`/videos/${v.id}`}
                  className="card-brut group block"
                  data-testid={`home-next-video-${v.id}`}
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={resolveFileUrl(v.cover_image)}
                      alt={v.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <span className="absolute top-3 left-3 bg-[var(--accent)] text-black font-mono-label text-[10px] px-2 py-1">
                      {(v.genre || "MUSIC").toUpperCase()}
                    </span>
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-14 h-14 bg-[var(--accent)] flex items-center justify-center">
                        <Play size={20} />
                      </div>
                    </div>
                  </div>
                  <div className="p-4 border-t border-neutral-800">
                    <div className="font-headline text-base uppercase truncate">{v.title}</div>
                    <div className="font-mono-label text-[10px] text-neutral-400 mt-1 flex justify-between">
                      <span className="truncate">{v.artist}</span>
                      <span>{v.duration}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TICKER */}
      {marquee.enabled !== false && marquee.text && (
        <section className="border-b border-neutral-800 bg-[var(--accent)] text-black" data-testid="home-marquee">
          <Marquee gradient={false} speed={marquee.speed || 60}>
            <span className="font-mono-label text-[11px] py-3 px-6" data-testid="home-marquee-text">
              {marquee.text}{"  "}
            </span>
          </Marquee>
        </section>
      )}

      {/* FEATURED MIXTAPE */}
      {featuredMixtape && (
        <section className="border-b border-neutral-800">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-16 grid md:grid-cols-2 gap-10 items-center">
            <div className="relative aspect-square border border-neutral-800 overflow-hidden">
              <img
                src={resolveFileUrl(featuredMixtape.cover_image)}
                alt={featuredMixtape.title}
                className="w-full h-full object-cover"
                data-testid="featured-mixtape-cover"
              />
              <div className="absolute top-4 left-4 chip chip-accent">FEATURED TAPE</div>
            </div>
            <div>
              <div className="font-mono-label text-[11px] text-[var(--accent)] mb-3">
                MIXTAPE / {featuredMixtape.genre} / {featuredMixtape.year}
              </div>
              <h2 className="font-display text-4xl md:text-6xl uppercase leading-none mb-4">
                {featuredMixtape.title}
              </h2>
              <div className="font-headline text-xl uppercase text-neutral-300 mb-6">
                by {featuredMixtape.artist}
              </div>
              <p className="text-neutral-400 mb-8 max-w-lg">{featuredMixtape.description}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => playList(contentToQueue(featuredMixtape, "mixtapes"))}
                  className="btn-primary"
                  data-testid="featured-play-btn"
                >
                  <Play size={16} /> Play Tape
                </button>
                <Link to={`/mixtapes/${featuredMixtape.id}`} className="btn-ghost" data-testid="featured-details-btn">
                  Tracklist <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* SPOTLIGHT BANNER — sits above LATEST DROPS so the editorial pick is the
          first thing visitors see on the catalog rail. */}
      {banner && (
        <section className="border-t border-neutral-800">
          <Link to={banner.link || "#"} className="block relative h-72 md:h-96 overflow-hidden" data-testid="home-banner">
            <img src={resolveFileUrl(banner.image)} alt={banner.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/30 to-transparent flex items-center px-12">
              <div>
                <div className="font-mono-label text-[11px] text-[var(--accent)] mb-3">SPOTLIGHT</div>
                <div className="font-display text-4xl md:text-6xl uppercase max-w-xl">{banner.title}</div>
              </div>
            </div>
          </Link>
        </section>
      )}

      {/* MIXTAPES STRIP */}
      <CarouselSection
        kicker="LATEST DROPS"
        title="MIXTAPES"
        items={mixtapes}
        link="/mixtapes"
        renderItem={(m) => (
          <Link to={`/mixtapes/${m.id}`} className="card-brut block group" data-testid={`home-mixtape-${m.id}`}>
            <div className="aspect-square overflow-hidden">
              <img src={resolveFileUrl(m.cover_image)} alt={m.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
            </div>
            <div className="p-4 border-t border-neutral-800">
              <div className="font-headline text-base uppercase truncate">{m.title}</div>
              <div className="font-mono-label text-[10px] text-neutral-400 mt-1">{m.artist}</div>
            </div>
          </Link>
        )}
      />

      {/* SINGLES STRIP */}
      <CarouselSection
        kicker="ROTATING HEAVY"
        title="TRENDING SINGLES"
        items={singles}
        link="/singles"
        renderItem={(s) => (
          <div className="card-brut group" data-testid={`home-single-${s.id}`}>
            <div className="relative aspect-square overflow-hidden">
              <img src={resolveFileUrl(s.cover_image)} alt={s.title} className="w-full h-full object-cover" />
              <button
                onClick={() => playSingle(s)}
                className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`play-single-${s.id}`}
                aria-label={`Play ${s.title}`}
              >
                <div className="w-14 h-14 bg-[var(--accent)] flex items-center justify-center">
                  <Play size={20} />
                </div>
              </button>
            </div>
            <div className="p-4 border-t border-neutral-800">
              <div className="font-headline text-base uppercase truncate">{s.title}</div>
              <div className="font-mono-label text-[10px] text-neutral-400 mt-1 flex justify-between">
                <span>{s.artist}</span>
                <span>{s.duration}</span>
              </div>
            </div>
          </div>
        )}
      />

      {/* ARTISTS */}
      {artists.length > 0 && (
        <section className="border-t border-neutral-800">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-16">
            <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">THE ROSTER</div>
            <h2 className="font-headline text-2xl md:text-3xl uppercase mb-8">Artists Building The Sound</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {artists.map((a) => (
                <Link key={a.id} to={`/artist/${a.username}`} className="card-brut p-5 flex items-center gap-4" data-testid={`home-artist-${a.username}`}>
                  {a.avatar ? (
                    <img src={resolveFileUrl(a.avatar)} alt={a.display_name} className="w-14 h-14 object-cover rounded-full border border-neutral-800" />
                  ) : (
                    <div className="w-14 h-14 bg-neutral-900 border border-neutral-800 rounded-full flex items-center justify-center"><Disc3 size={20} /></div>
                  )}
                  <div className="min-w-0">
                    <div className="font-headline text-sm uppercase truncate">{a.display_name}</div>
                    <div className="font-mono-label text-[10px] text-neutral-400">@{a.username}{a.verified && " · VERIFIED"}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* MERCH STRIP */}
      <CarouselSection
        kicker="SHOP THE LOOK"
        title="MERCH & VINYL"
        items={products.filter((p) => p.category === "merch")}
        link="/merch"
        renderItem={(p) => (
          <Link to={`/merch/${p.id}`} className="card-brut block group" data-testid={`home-product-${p.id}`}>
            <div className="aspect-[4/5] overflow-hidden">
              <img src={resolveFileUrl(p.image)} alt={p.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
            </div>
            <div className="p-4 border-t border-neutral-800 flex items-center justify-between">
              <div>
                <div className="font-headline text-sm uppercase truncate max-w-[140px]">{p.title}</div>
                <div className="font-mono-label text-[10px] text-neutral-400">{p.category.toUpperCase()}</div>
              </div>
              <div className="font-display text-2xl">${p.price.toFixed(0)}</div>
            </div>
          </Link>
        )}
      />

      {/* Culture Connect featured */}
      {(featured.creatives?.length > 0 || featured.talent?.length > 0 || featured.top_rated?.length > 0) && (
        <section className="border-t border-neutral-800" data-testid="home-cc-featured">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-16 space-y-12">
            <FeaturedRow title="Featured Creatives" kicker="HIRE THE TALENT" rows={featured.creatives} />
            <FeaturedRow title="Featured Talent" kicker="DIRECTLY ON CULTURE CONNECT" rows={featured.talent} />
            {featured.trending?.length > 0 && <FeaturedRow title="Trending" kicker="MOST BOOKED · LAST 30 DAYS" rows={featured.trending} />}
            {featured.top_rated?.length > 0 && <FeaturedRow title="Top Rated" kicker="HIGHEST REVIEW SCORES" rows={featured.top_rated} />}
            {featured.recently_hired?.length > 0 && <FeaturedRow title="Recently Hired" kicker="LOCKED IN THIS WEEK" rows={featured.recently_hired} />}
          </div>
        </section>
      )}

      {/* Newsletter strip — Culture Club */}
      <section className="border-t border-neutral-800 bg-gradient-to-r from-[#1a0a05] via-black to-black" data-testid="home-newsletter-strip">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">CULTURE CLUB · NEWSLETTER</div>
            <h3 className="font-display text-3xl sm:text-4xl uppercase leading-tight">Get the drop in your inbox.</h3>
            <p className="text-neutral-400 text-sm mt-2 max-w-xl">Interviews, breaking news, giveaways, and event drops — one or two emails a week, never spam.</p>
          </div>
          <Link to="/culture-club" className="btn-primary" data-testid="home-join-culture-club">Join The Culture</Link>
        </div>
      </section>
    </div>
  );
}

function CarouselSection({ kicker, title, items, link, renderItem }) {
  if (!items?.length) return null;
  return (
    <section className="border-t border-neutral-800">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">{kicker}</div>
            <h2 className="font-headline text-2xl md:text-3xl uppercase">{title}</h2>
          </div>
          <Link to={link} className="font-mono-label text-[11px] text-neutral-300 hover:text-[var(--accent)] inline-flex items-center gap-2">
            VIEW ALL <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.slice(0, 4).map((it) => (
            <React.Fragment key={it.id}>{renderItem(it)}</React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedRow({ title, kicker, rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div data-testid={`featured-row-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">{kicker}</div>
          <h2 className="font-headline text-2xl sm:text-3xl uppercase">{title}</h2>
        </div>
        <Link to="/culture-connect" className="font-mono-label text-[10px] text-neutral-400 hover:text-white">VIEW ALL →</Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        {rows.slice(0, 5).map((u) => (
          <Link key={u.id} to={`/profile/${u.username}`} className="card-brut p-4 hover:border-[var(--accent)] transition-colors" data-testid={`featured-${u.username}`}>
            <div className="flex items-center gap-3 mb-3">
              {u.avatar ? (
                <img src={resolveFileUrl(u.avatar)} alt={u.display_name} className="w-12 h-12 object-cover border border-neutral-800" />
              ) : (
                <div className="w-12 h-12 border border-neutral-800 bg-neutral-900 flex items-center justify-center font-display text-lg">{u.display_name.slice(0, 2).toUpperCase()}</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-headline text-sm uppercase truncate">{u.display_name}</div>
                <div className="font-mono-label text-[9px] text-neutral-500 truncate">{u.service_title || `@${u.username}`}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-1">
              {u.account_type && <span className="font-mono-label text-[9px] text-neutral-400 border border-neutral-800 px-1.5 py-0.5">{u.account_type.toUpperCase()}</span>}
              {u.rate_amount > 0 && <span className="font-mono-label text-[9px] text-[var(--accent)] border border-[var(--accent)] px-1.5 py-0.5">${u.rate_amount}/{u.rate_unit || "hr"}</span>}
              {u.rating_avg > 0 && <span className="font-mono-label text-[9px] text-neutral-400 border border-neutral-800 px-1.5 py-0.5">★ {u.rating_avg.toFixed(1)}</span>}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

